import { useCallback, useContext, useEffect, useMemo } from "react";
import { Asset } from "contentful";
import Head from "next/head";
import { useRouter } from "next/router";
import allPaths, {
  threeOptionProducts,
  twoOptionProducts,
} from "src/lib/components/ProductForm/configTest/configTestProductPaths";
import { useDefaultVariant } from "src/lib/components/ProductForm/hooks/useDefaultVariant";
import VerticalSpacingContext from "src/lib/context/VerticalSpacingContext";
import useFeatureFlag from "src/lib/hooks/useFeatureFlag";
import { getShopifyId } from "src/lib/services/util/shopifyID";
import { isOptionConfigMatching } from "src/lib/util/optionMatch";

import {
  IPageMetadataFields,
  IShopifyProductFields,
} from "../../../@types/generated/contentful";
import useLogger from "../../hooks/useLogger";
import {
  AffirmContext,
  CartContext,
  RenderSections,
} from "../../lib/components";
import cartUtils from "../../lib/components/Cart/utils";
import { LinkPreviewMeta } from "../../lib/components/ContentfulSEO/ContentfulSEO";
import { ProductForm } from "../../lib/components/ProductForm";
import { CrossingMindsContext } from "../../lib/context/CrossingMindsContext";
import { ProductContextProvider } from "../../lib/context/ProductContext";
import variantRedirectMap from "../../lib/data/variantRedirectMap";
import variantReplacements from "../../lib/data/variantReplacements";
import virtualProductMap from "../../lib/data/virtualProductMap";
import useCatalog from "../../lib/hooks/useCatalog";
import useTypedMutation from "../../lib/hooks/useTypedMutation";
import { useTypedQuery } from "../../lib/hooks/useTypedQuery";
import { CartMutation } from "../../lib/queries/data/cartData";
import { CustomField, MergedProduct } from "../../lib/queries/data/productData";
import { QueryData } from "../../lib/queries/server";
import { MergedProductVariant } from "../../lib/services/catalog/types";
import { addViewProductEvent } from "../../lib/services/elevar/events";
import {
  composePaths,
  composeProps,
  PageProps,
} from "../../lib/services/util/compose";
import { MergedOption } from "../../lib/services/util/optionRendering";
import { getCurrentCanonicalVariant, isTruthy } from "../../lib/util";
import { CanonicalUrlContext } from "../../lib/util/getCanonical";
import { filterListByLocales, LocaleCode } from "../../lib/util/locale";
import {
  makePageDescription,
  makePageTitle,
} from "../../lib/util/makePageMeta";
import makeProductPageData from "../../lib/util/makeProductPageData";
import makeProductUrl from "../../lib/util/makeProductUrl";
import { toKeyValue } from "../../lib/util/toKeyValue";

export const getStaticPaths = composePaths(async (context, { queryClient }) => {
  const paths = await queryClient.fetchQuery<QueryData<"productPaths">>([
    "productPaths",
  ]);

  const configuredLocales = context.locales as LocaleCode[] | undefined;

  return {
    paths:
      paths
        .flatMap(({ slug: path, variants }) => {
          const allLocales = Object.keys(path || {});
          const locales = filterListByLocales(allLocales, configuredLocales);
          return locales.flatMap((locale) => {
            const handle = path[locale];
            const variantIds = variants[locale];

            if (!handle || !variantIds) return undefined;
            return variantIds.map((variant) => ({
              params: { slug: [handle, variant] },
              locale,
            }));
          });
        })
        .filter(isTruthy) ?? [],
    fallback: "blocking",
  };
});

const getSelectedMeta = (
  product: MergedProduct,
  variant: MergedProductVariant
) => {
  const selectedOptions = Object.fromEntries(
    variant.selectedOptions.map((o) => [o.name.toLowerCase(), o.value])
  );
  const optionWithGroups = product.options.find((o) => o.groups?.length);
  const optionNameLower = optionWithGroups?.name.toLowerCase();
  const selectedValue = optionNameLower
    ? selectedOptions[optionNameLower]
    : undefined;

  return { selectedOptions, selectedValue, optionWithGroups };
};

const findFacetGroup = (
  groups: MergedOption["groups"] | undefined,
  selectedValue: string | undefined
) => {
  if (!groups) return null;
  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i];
    if (group.groups && findFacetGroup(group.groups, selectedValue))
      return group;
    if (group.values?.some((valueObj) => valueObj.value === selectedValue))
      return group;
    if (group.name === selectedValue) return group;
  }

  return null;
};

const makeProductPageMeta = (
  product: MergedProduct,
  variant: MergedProductVariant,
  metadata?: IPageMetadataFields | null
) => {
  const { selectedOptions, optionWithGroups, selectedValue } = getSelectedMeta(
    product,
    variant
  );

  const facetGroup = findFacetGroup(optionWithGroups?.groups, selectedValue);
  const facet = facetGroup?.label || facetGroup?.name;
  const tokens = {
    ...selectedOptions,
    product: product.title,
    category: product.breadcrumbs[0].text,
    facet,
  };

  const title = metadata?.title || makePageTitle(tokens);
  const description = metadata?.description || makePageDescription();

  const {
    images: [firstVariantImage],
  } = variant;
  const variantImageAsAsset = {
    sys: {
      id: variant.id,
    },
    fields: {
      title: firstVariantImage.title,
      description: firstVariantImage.description,
      file: {
        url: firstVariantImage.url,
        details: {
          image: {
            height: firstVariantImage.height,
            width: firstVariantImage.width,
          },
        },
      },
    },
  } as Asset;

  const image = metadata?.images?.[0] || variantImageAsAsset;
  const imageUrl = image.fields.file.url;

  return { title, description, image, imageUrl };
};

type RawProduct = NonNullable<Awaited<QueryData<"productData">>>["product"];
type AddOn = NonNullable<Awaited<QueryData<"productData">>>["addOn"] & {
  header: string;
};
type CanonicalFields = Pick<
  RawProduct,
  "canonicalOptionNames" | "canonicalVariantIds"
>;
type FeaturedContentFields = Pick<RawProduct, "featuredContent">;
type FullProduct = MergedProduct & CanonicalFields & FeaturedContentFields;

const makeCanonicalUrl = (
  {
    canonicalOptionNames,
    canonicalVariantIds,
    variants,
    handle,
  }: Pick<MergedProduct, "variants" | "handle"> & CanonicalFields,
  currentVariant: MergedProductVariant,
  defaultVariantID: MergedProductVariant["id"]
) => {
  const canonicalVariants = canonicalVariantIds
    .map((id) => variants.find((v) => v.id === id))
    .filter(isTruthy);
  const currentCanonicalVariant = getCurrentCanonicalVariant(
    canonicalOptionNames,
    canonicalVariants,
    currentVariant
  );

  const canonicalUrl =
    currentCanonicalVariant && currentCanonicalVariant.id !== defaultVariantID
      ? makeProductUrl(handle, currentCanonicalVariant.id)
      : null;

  return canonicalUrl;
};

export const getStaticProps = composeProps(
  async ({ params, locale }, { queryClient }) => {
    const [handle = "", rawVariantId, ...rest] =
      (Array.isArray(params?.slug) && params?.slug) || [];

    // 404 if we get more than the expected number of params
    if (rest.length > 0) {
      return {
        notFound: true,
      };
    }

    const initialVariantId =
      rawVariantId && rawVariantId !== "default" ? rawVariantId : undefined;

    const productVariantRedirect = variantRedirectMap[handle];
    if (productVariantRedirect && initialVariantId) {
      const mappedVariantId = productVariantRedirect[initialVariantId];
      if (mappedVariantId)
        return {
          redirect: {
            destination: makeProductUrl(handle, mappedVariantId),
            permanent: true,
          },
        };
    }

    const localeCode = locale as LocaleCode;
    const virtualProduct = virtualProductMap[localeCode][handle];
    if (virtualProduct) {
      const virtualVariantId =
        initialVariantId || virtualProduct.defaultVariant;

      return {
        redirect: {
          destination: makeProductUrl(virtualProduct.handle, virtualVariantId),
          permanent: false,
        },
      };
    }

    const data = await queryClient.fetchQuery<QueryData<"productData">>([
      "productData",
      handle,
    ]);

    if (!data) {
      return {
        notFound: true,
      };
    }

    const variantId = initialVariantId || data.product.defaultVariant;
    const replacementMatch = variantReplacements?.find(
      ({ original }) => original.variantId === variantId
    );
    if (replacementMatch) {
      return {
        redirect: {
          destination: makeProductUrl(
            replacementMatch.replacement.slug,
            replacementMatch.replacement.variantId
          ),
          permanent: false,
        },
      };
    }

    const variant = data.product.variants.find(({ id }) => id === variantId);
    const defaultVariantB = data.product.variants.find(
      ({ id }) => id === getShopifyId(data.product.defaultVariantB ?? "")
    );

    // If the product exists but the variant doesn't,
    // we want to redirect to the default variant.
    // We do this temporarily because we could revive the variant in the future.
    if (!variant) {
      return {
        redirect: {
          destination: makeProductUrl(
            data.product.handle,
            data.product.defaultVariant
          ),
          permanent: false,
        },
      };
    }

    const canonicalUrl = makeCanonicalUrl(
      data.product,
      variant,
      data.product.defaultVariant
    );

    const { title, description, image } = makeProductPageMeta(
      data.product as MergedProduct,
      variant
    );

    const originalDescription = data.pageMetadata?.description ?? null;

    // instantiate empty object if missing
    if (!data.pageMetadata) data.pageMetadata = {} as IPageMetadataFields;

    // generate title if missing
    if (!data.pageMetadata.title) {
      data.pageMetadata.title = title;
      data.pageMetadata.appendSiteTitle = false;
    }

    // generate description if missing
    if (!data.pageMetadata.description) {
      data.pageMetadata.description = description;
    }

    // insert variant image if missing
    if (!data.pageMetadata.images) {
      data.pageMetadata.images = [image];
    }

    // trim data from other variants
    const trimmedVariants = data.product.variants.map((v) => {
      if (v.id === variant.id) return v;

      return {
        id: v.id,
        selectedOptions: v.selectedOptions,
        images: [],
      };
    });

    const {
      page: { sections },
      ...dataWithoutPage
    } = data;

    return {
      props: {
        ...dataWithoutPage,
        sections,
        product: {
          ...dataWithoutPage.product,
          variants: trimmedVariants,
        },
        addOn: {
          header: data.addOnHeader,
          ...data.addOn,
        },
        originalDescription,
        variant,
        defaultVariantB: defaultVariantB ?? null,
        canonicalUrl,
      },
      revalidate: variant.available ? 24 * 60 * 60 : 6 * 60 * 60,
    };
  }
);

export interface ProductProps {
  sections: IShopifyProductFields["sections"];
  originalDescription: string;
  product: FullProduct;
  addOn: AddOn;
  variant: MergedProductVariant;
  defaultVariantB: MergedProductVariant | null;
  customFields: CustomField[];
}

export default function ProductPage({
  product,
  sections,
  financingConfig,
  variant,
  defaultVariantB,
  customFields,
  layoutData: { premiumShipping },
  pageMetadata,
  addOn,
}: PageProps<ProductProps>) {
  const router = useRouter();
  const verticalSpacing = useMemo(() => ({ hasVerticalSpacing: false }), []);
  const [currentVariant, setCurrentVariant] = useDefaultVariant(
    variant,
    defaultVariantB
  );
  const setCartOpen = useContext(CartContext);
  const { setCanonical } = useContext(CanonicalUrlContext);
  const { recordCustomItemInteraction } = useContext(CrossingMindsContext);

  const { catalog } = useCatalog([product.id]);
  const catalogProduct = catalog?.[product.id];

  const { data: cart } = useTypedQuery(["cart"]);
  const { logger } = useLogger();

  // If the variant from on high changes, we need to update the currentVariant
  useEffect(() => {
    setCurrentVariant(variant);
  }, [variant, setCurrentVariant]);

  useEffect(() => {
    addViewProductEvent({
      product,
      variant: currentVariant,
      list: "",
    });
    // Including product in here causes a double firing, since the product changing also updates currentVariant, this is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVariant]);

  const { mutate: addToCartMutation } = useTypedMutation(
    [CartMutation.AddToCart],
    {
      onError: (error) =>
        cartUtils.onCartError(
          error,
          logger,
          cart,
          "Error on adding item to cart"
        ),
    }
  );

  const handleAddToCart = ({
    location,
    ...rawCustomAttributes
  }: Record<string, string>) => {
    const customAttributes = toKeyValue(rawCustomAttributes);
    const selectedOptions = Object.fromEntries(
      (currentVariant?.selectedOptions ?? []).map(({ name, value }) => [
        name,
        value,
      ])
    );
    const activeBadge = product?.badges?.find((badge) =>
      isOptionConfigMatching(badge.optionMatchConfigs, selectedOptions)
    );
    // Here is where you'd be passing SomeNewPayload instead of the arguments that are currently passed.
    addToCartMutation({
      location,
      locale: router.locale as LocaleCode,
      lineItems: [
        {
          customAttributes,
          badgeText: activeBadge?.text,
          product: {
            ...product,
            id: currentVariant.productId,
          },
          quantity: 1,
          variant: currentVariant,
        },
      ],
    });
    setCartOpen(true);
    recordCustomItemInteraction({
      itemId: currentVariant.id,
      interactionType: "add_to_cart",
    });
  };

  const navigate = useCallback(
    (variantId: string) => {
      // Update pathname
      const url = new URL(window.location.href);
      // TODO This is not locale aware.
      url.searchParams.set("variant", variantId);
      const pathname = url.href.slice(url.origin.length).replace(/#.*/, "");
      // Update variant state

      const catalogVariant = catalogProduct?.variants[variantId];
      if (!catalogVariant) {
        router.replace(pathname, undefined);
        return;
      }

      router.replace(pathname, undefined, { shallow: true });
      setCurrentVariant(catalogVariant as unknown as MergedProductVariant);
    },
    [router, setCurrentVariant, catalogProduct]
  );

  useEffect(() => {
    const canonicalUrl = makeCanonicalUrl(
      product,
      currentVariant,
      product.defaultVariant
    );
    setCanonical(canonicalUrl);

    return () => {
      setCanonical(null);
    };
  }, [product, currentVariant, setCanonical]);

  const { structuredData, meta } = useMemo(
    () => ({
      structuredData: makeProductPageData(product, currentVariant.id),
      meta: makeProductPageMeta(product, currentVariant, pageMetadata),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentVariant, pageMetadata]
  );

  const { optionWithGroups, selectedValue } = getSelectedMeta(
    product,
    currentVariant
  );
  const activeGroup = findFacetGroup(optionWithGroups?.groups, selectedValue);
  const activeGroupTitle = activeGroup?.label || activeGroup?.name;

  // Temporary title joining as we only use this on The Bed
  // Will bring separate field to Contentful: https://app.asana.com/0/1201817617363479/1207455872585348/f
  const title = optionWithGroups?.useActiveGroupTitle
    ? `${product.title} ${activeGroupTitle?.replace(
        /headboard|pillowboard/i,
        "+ $&"
      )}`
    : undefined;

  useEffect(() => {
    if (currentVariant.id)
      recordCustomItemInteraction({
        itemId: currentVariant.id,
        interactionType: "product_page_view",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVariant.id]);

  const { value: productConfigTestFlag } = useFeatureFlag(
    "product-option-config-test"
  );

  const pathWithoutQuery = router.asPath.split("?")[0];

  const memoizedProduct = useMemo(() => {
    if (
      productConfigTestFlag === "variant" &&
      allPaths.includes(pathWithoutQuery)
    ) {
      return {
        ...product,
        options: product.options.reduce<typeof product.options>(
          (acc, cur, i) =>
            (i === 1 && twoOptionProducts.includes(pathWithoutQuery)) ||
            (i === 2 && threeOptionProducts.includes(pathWithoutQuery))
              ? [cur, ...acc]
              : [...acc, cur],
          []
        ),
      };
    }
    return product;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.title, productConfigTestFlag, pathWithoutQuery]);

  return (
    <ProductContextProvider product={memoizedProduct} variant={currentVariant}>
      <LinkPreviewMeta
        title={meta.title}
        description={meta.description}
        image={meta.imageUrl}
      />
      <Head>
        <script
          id="product-seo"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      </Head>

      <AffirmContext.Provider value={financingConfig.affirmKey}>
        <ProductForm
          breadcrumbs={product.breadcrumbs}
          customFields={customFields}
          displayImagesAs={product.displayImagesAs}
          featuredItemsTitle={product.featuredContent?.title}
          miniFormTitle={title}
          onAddToCart={handleAddToCart}
          onVariantChange={navigate}
          premiumShippingMessaging={premiumShipping}
          hasSections={Boolean(sections?.length)}
          detailAccordions={product.detailAccordions}
          addOn={{
            ...addOn,
            id: addOn.id ?? "",
            parentOptions: currentVariant.selectedOptions,
          }}
        />
      </AffirmContext.Provider>
      {sections && (
        <div className="mt-12 full:mt-16">
          <VerticalSpacingContext.Provider value={verticalSpacing}>
            <RenderSections sections={sections} />
          </VerticalSpacingContext.Provider>
        </div>
      )}
    </ProductContextProvider>
  );
}
